/**
 * 배관 검토 계산서.
 *
 * 근거: API Standard 520 Part II, 6판(2015)
 *   입구 3 % 7.3.4 · 손실 항목 7.3.7.2 · 유량 기준 7.3.7.3 ·
 *   힘 평형 대안 7.3.6 d) · 배압 5.3 · 반력 식 (1)
 *
 * 사이징 계산서와 같은 서식을 쓰되 **한 계통에 한 장**이다.
 * 밸브별로 나오는 사이징 계산서와 성격이 다르므로 시트를 따로 둔다.
 */
import { FITTINGS } from "../calc/fittings";
import { INLET_LOSS_LIMIT_PERCENT } from "../calc/inletLoss";
import { gaugeLabel, type PressureUnit } from "./pressureUnit";
import { MATERIAL_LABELS } from "./pipingState";
import {
  runPiping,
  type PipeRunState,
  type PipingState,
  type RunHydraulics,
} from "./pipingState";

const nf = (v: number, d = 1) =>
  Number.isFinite(v)
    ? v.toLocaleString("ko-KR", {
        minimumFractionDigits: d,
        maximumFractionDigits: d,
      })
    : "—";

const REGIME_LABEL = {
  laminar: "층류",
  transitional: "천이",
  turbulent: "난류",
} as const;

/** 배관 한 구간의 사양·결과 — 입구와 출구가 같은 서식을 쓴다. */
function RunBlock({
  title,
  spec,
  result,
}: {
  title: string;
  spec: PipeRunState;
  result: RunHydraulics | null;
}) {
  return (
    <>
      <h4 className="sheet__h4">{title}</h4>
      <table className="sheet__table">
        <tbody>
          <tr>
            <th>관경</th>
            <td>
              {spec.useCustomId
                ? `내경 직접 입력 ${spec.customIdMm} mm`
                : `${spec.nps}" Sch ${spec.schedule}`}
              {result && (
                <span className="muted">
                  {" "}
                  (내경 {nf(result.innerDiameterMm, 2)} mm)
                </span>
              )}
            </td>
            <th>재질·조도</th>
            <td>{MATERIAL_LABELS[spec.material]}</td>
          </tr>
          <tr>
            <th>직관 길이</th>
            <td>{spec.straightLengthM} m</td>
            <th>계산 길이</th>
            <td>
              {result ? `${nf(result.totalLengthM, 2)} m` : "—"}
              {result && (
                <span className="muted">
                  {" "}
                  (부속 등가 {nf(result.equivalentLengthM, 2)} m)
                </span>
              )}
            </td>
          </tr>
        </tbody>
      </table>

      {spec.fittings.length > 0 && (
        <table className="sheet__table sheet__table--grid">
          <thead>
            <tr>
              <th>부속</th>
              <th>개수</th>
              <th>L/D</th>
              <th>등가길이 (m)</th>
              <th>K</th>
            </tr>
          </thead>
          <tbody>
            {spec.fittings.map((f) => {
              const s = FITTINGS[f.kind];
              const row = result?.breakdown.find((b) => b.kind === f.kind);
              return (
                <tr key={f.kind}>
                  <td>{s.label}</td>
                  <td className="cell">{f.count}</td>
                  <td className="cell">{s.lOverD ?? "—"}</td>
                  <td className="cell">
                    {row ? nf(row.equivalentLengthM, 2) : "—"}
                  </td>
                  <td className="cell">{row ? nf(row.k, 2) : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {result && (
        <table className="sheet__table sheet__table--grid">
          <thead>
            <tr>
              <th>항목</th>
              <th>값</th>
              <th>근거</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>유속{result.compressible && " (출구단)"}</td>
              <td className="cell">{nf(result.velocityMs, 2)} m/s</td>
              <td className="muted">
                {result.compressible
                  ? "v = W / (ρ·A), ρ = pM/(ZRT) — 압력이 가장 낮은 관 끝 기준"
                  : "v = W / (ρ·A)"}
              </td>
            </tr>
            <tr>
              <td>레이놀즈수</td>
              <td className="cell">
                {result.reynolds.toExponential(2)} ·{" "}
                {REGIME_LABEL[result.flowRegime]}
              </td>
              <td className="muted">Re = 4W / (π·d·μ)</td>
            </tr>
            <tr>
              <td>마찰계수 f</td>
              <td className="cell">{result.frictionFactor.toFixed(4)}</td>
              <td className="muted">Colebrook–White</td>
            </tr>
            {result.compressible && (
              <>
                <tr>
                  <td>출구단 마하수</td>
                  <td className="cell">
                    {nf(result.compressible.outletMach, 3)}
                    <span className="muted">
                      {" "}
                      (입구단 {nf(result.compressible.inletMach, 3)})
                    </span>
                  </td>
                  <td className="muted">
                    Ma ≤ 0.8, 0.6 이하 바람직 (KOSHA D-63 5.2.6.2)
                  </td>
                </tr>
                <tr>
                  <td>출구단 밀도</td>
                  <td className="cell">
                    {nf(result.compressible.outletDensityKgM3, 3)} kg/m³
                  </td>
                  <td className="muted">ρ = pM/(ZRT)</td>
                </tr>
                <tr>
                  <td>관 양단 절대압</td>
                  <td className="cell">
                    {nf(result.compressible.inletPressureKpaAbs, 1)} →{" "}
                    {nf(result.compressible.outletPressureKpaAbs, 1)} kPa(a)
                  </td>
                  <td className="muted">
                    하류압에서 거슬러 올라가 관 입구압을 구한다
                  </td>
                </tr>
                <tr>
                  <td>초크 한계압</td>
                  <td className="cell">
                    {nf(result.compressible.criticalPressureKpaAbs, 1)} kPa(a)
                  </td>
                  <td className="muted">
                    관 출구압이 이 값보다 낮으면 초크 (Ma = 1)
                  </td>
                </tr>
              </>
            )}
            <tr>
              <td>ΣK (길이 미환산)</td>
              <td className="cell">{nf(result.fittingK, 2)}</td>
              <td className="muted">입구·출구 손실 (7.3.7.2 a)</td>
            </tr>
            <tr>
              <td>
                <b>압력손실</b>
              </td>
              <td className="cell">
                <b>{nf(result.dropKpa, 2)} kPa</b>
                <span className="muted">
                  {" "}
                  (길이 몫 {nf(result.frictionKpa, 2)} + ΣK 몫{" "}
                  {nf(result.fittingKpa, 2)})
                </span>
              </td>
              <td className="muted">
                {result.compressible
                  ? "등온 압축성 — API 521 식 (33) = KOSHA D-63 식 (5-12)"
                  : "Darcy–Weisbach"}
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </>
  );
}

export function PipingSheet({
  value,
  unit,
  today,
}: {
  value: PipingState;
  unit: PressureUnit;
  today: string;
}) {
  const out = runPiping(value, unit);

  return (
    <section className="card sheet">
      <header className="sheet__head">
        <div>
          <h2 className="sheet__title">배관 검토 계산서</h2>
          <p className="sheet__sub">API 520 Part II 6판(2015) · 7.3 · 5.3</p>
        </div>
        <div className="sheet__meta">
          <div>
            <dt>태그</dt>
            <dd className="sheet__tag">{value.tag || "—"}</dd>
          </div>
          <div>
            <dt>작성일</dt>
            <dd>{today}</dd>
          </div>
        </div>
      </header>

      <h3 className="sheet__h3">1. 검토 조건</h3>
      <table className="sheet__table">
        <tbody>
          <tr>
            <th>설정압</th>
            <td>
              {value.setPressure} {gaugeLabel(unit)}
            </td>
            <th>MAWP</th>
            <td>
              {value.mawp} {gaugeLabel(unit)}
            </td>
          </tr>
          <tr>
            <th>허용 축적</th>
            <td>{value.allowableAccumulationPercent} %</td>
            <th>밸브 형식</th>
            <td>
              {value.valveType === "conventional"
                ? "일반형"
                : value.valveType === "balanced-bellows"
                  ? "밸런스드 벨로우즈"
                  : "파일럿형"}
            </td>
          </tr>
          <tr>
            <th>유량</th>
            <td>
              {value.flowKgH} kg/h
              <span className="muted">
                {" "}
                ({value.flowBasis === "rated" ? "정격 용량" : "소요 유량"},
                7.3.7.3)
              </span>
            </td>
            <th>
              밀도 · 점도
              {value.outletCompressible && (
                <span className="muted"> (입구 배관 기준)</span>
              )}
            </th>
            <td>
              {value.densityKgM3} kg/m³ · {value.viscosityCp} cP
            </td>
          </tr>
          <tr>
            <th>손실 계산 방식</th>
            <td>
              {value.method === "equivalent-length"
                ? "등가길이 (L/D)"
                : "저항계수 (K)"}
            </td>
            <th>3 % 기준 예외</th>
            <td>
              {value.exemption === "none"
                ? "해당 없음"
                : value.exemption === "thermal-relief"
                  ? "열팽창 전용 (7.3.8)"
                  : "원격감지 파일럿형 (7.3.9)"}
            </td>
          </tr>
        </tbody>
      </table>

      {!out.ok ? (
        <p className="sheet__note sheet__note--warn">계산 불가 — {out.message}</p>
      ) : (
        <>
          <h3 className="sheet__h3">2. 입구 배관</h3>
          <RunBlock title="사양 및 손실" spec={value.inlet} result={out.inlet} />

          {out.inletCheck?.ok && (
            <>
              <h4 className="sheet__h4">
                3 % 기준 판정 (API 520 Part II 7.3.4)
              </h4>
              <table className="sheet__table sheet__table--grid">
                <tbody>
                  <tr>
                    <td>배관 비회복 손실</td>
                    <td className="cell">
                      {nf(out.inletCheck.pipingLossKpa, 2)} kPa
                    </td>
                    <td className="muted">7.3.7.2</td>
                  </tr>
                  <tr>
                    <td>상류 파열판 압력강하</td>
                    <td className="cell">
                      {nf(out.inletCheck.ruptureDiskLossKpa, 2)} kPa
                    </td>
                    <td className="muted">7.3.7.2 b)</td>
                  </tr>
                  <tr>
                    <td>
                      <b>총 손실</b>
                    </td>
                    <td className="cell">
                      <b>{nf(out.inletCheck.totalLossKpa, 2)} kPa</b>
                    </td>
                    <td className="muted">합계</td>
                  </tr>
                  <tr>
                    <td>설정압 대비</td>
                    <td className="cell">
                      {nf(out.inletCheck.lossPercent, 2)} %
                    </td>
                    <td className="muted">
                      기준 {INLET_LOSS_LIMIT_PERCENT} % 이내
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <b>판정</b>
                    </td>
                    <td className="cell">
                      <b>{out.inletCheck.withinLimit ? "적합" : "부적합"}</b>
                    </td>
                    <td className="muted">7.3.4</td>
                  </tr>
                </tbody>
              </table>

              {out.forceBalance && (
                <>
                  <h4 className="sheet__h4">
                    힘 평형에 의한 대안 판정 (7.3.6 d)
                  </h4>
                  <p className="sheet__note">{out.forceBalance.criterion}</p>
                  <table className="sheet__table sheet__table--grid">
                    <tbody>
                      <tr>
                        <td>좌변</td>
                        <td className="cell">
                          {nf(out.forceBalance.leftSide, 2)} kPa
                        </td>
                      </tr>
                      <tr>
                        <td>우변</td>
                        <td className="cell">
                          {nf(out.forceBalance.rightSide, 2)} kPa
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <b>판정</b>
                        </td>
                        <td className="cell">
                          <b>{out.forceBalance.passes ? "만족" : "불만족"}</b>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </>
              )}
            </>
          )}

          <h3 className="sheet__h3">3. 출구 배관</h3>
          <RunBlock title="사양 및 손실" spec={value.outlet} result={out.outlet} />
          <p className="sheet__note">
            출구 압력손실이 곧 축적배압입니다 — 아래 판정에 그대로 쓰입니다.
          </p>
          <p className="sheet__note">
            {value.outletCompressible ? (
              <>
                출구 배관은 <b>등온 압축성</b>으로 계산했습니다 (KOSHA GUIDE
                D-63-2018 4.1(1)). 분자량 {value.outletMolecularWeight} g/mol,
                배출 온도 {value.outletTemperatureK} K, 압축계수{" "}
                {value.outletCompressibility} 를 적용했으며, 밀도는 이
                값들로부터 지점마다 다시 구하므로 위 &apos;유체 조건&apos;의
                밀도 입력은 출구 배관에 쓰이지 않습니다. 부속 저항은 마찰계수로
                나눠 등가길이로 환산해 더했습니다.
              </>
            ) : (
              <>
                출구 배관은 <b>비압축성</b>(Darcy–Weisbach)으로 계산했습니다.
                KOSHA GUIDE D-63-2018 4.1(1) 은 배출배관을 등온 과정으로
                보라고 하므로, 기체 배출이고 배관이 길다면 이 결과는 압력손실을
                과소평가할 수 있습니다.
              </>
            )}
          </p>

          {out.backpressure?.ok && (
            <>
              <h3 className="sheet__h3">4. 배압 판정</h3>
              <p className="sheet__note">{out.backpressure.criterion}</p>
              <table className="sheet__table sheet__table--grid">
                <tbody>
                  <tr>
                    <td>중첩배압</td>
                    <td className="cell">
                      {value.superimposed} {gaugeLabel(unit)}
                    </td>
                    <td className="muted">입력</td>
                  </tr>
                  <tr>
                    <td>축적배압 (출구 손실)</td>
                    <td className="cell">
                      {nf(out.outlet?.dropKpa ?? Number.NaN, 2)} kPa
                    </td>
                    <td className="muted">3항 계산 결과</td>
                  </tr>
                  <tr>
                    <td>총 배압</td>
                    <td className="cell">
                      {nf(out.backpressure.totalKpaGauge, 2)} kPag
                    </td>
                    <td className="muted">중첩 + 축적</td>
                  </tr>
                  <tr>
                    <td>총 배압 / 설정압</td>
                    <td className="cell">
                      {nf(out.backpressure.totalPercentOfSet, 1)} %
                    </td>
                    <td className="muted">5.3.3</td>
                  </tr>
                  <tr>
                    <td>허용 축적배압</td>
                    <td className="cell">
                      {out.backpressure.allowableBuiltUpKpa === null
                        ? "— (형식별 다른 기준)"
                        : `${nf(out.backpressure.allowableBuiltUpKpa, 2)} kPa`}
                    </td>
                    <td className="muted">API 520 Part I 식 (1)</td>
                  </tr>
                  <tr>
                    <td>
                      <b>판정</b>
                    </td>
                    <td className="cell">
                      <b>
                        {out.backpressure.builtUpWithinLimit &&
                        out.backpressure.totalWithinLimit
                          ? "적합"
                          : "부적합"}
                      </b>
                    </td>
                    <td className="muted">5.3</td>
                  </tr>
                </tbody>
              </table>
            </>
          )}

          {value.reaction.enabled && out.reaction?.ok && (
            <>
              <h3 className="sheet__h3">5. 대기 방출 반력</h3>
              <table className="sheet__table sheet__table--grid">
                <tbody>
                  <tr>
                    <td>비열비 k · 정체온도 T · 분자량 M</td>
                    <td className="cell">
                      {value.reaction.specificHeatRatio} ·{" "}
                      {value.reaction.stagnationTemperatureK} K ·{" "}
                      {value.reaction.molecularWeight}
                    </td>
                    <td className="muted">입력</td>
                  </tr>
                  <tr>
                    <td>방출점 내경 · 방출 직전 정압</td>
                    <td className="cell">
                      {value.reaction.outletIdMm} mm ·{" "}
                      {value.reaction.outletStaticPressure} {gaugeLabel(unit)}
                    </td>
                    <td className="muted">입력</td>
                  </tr>
                  <tr>
                    <td>운동량 몫</td>
                    <td className="cell">{nf(out.reaction.momentumN, 0)} N</td>
                    <td className="muted">{out.reaction.equation}</td>
                  </tr>
                  <tr>
                    <td>정압 몫</td>
                    <td className="cell">{nf(out.reaction.staticN, 0)} N</td>
                    <td className="muted">A·P</td>
                  </tr>
                  <tr>
                    <td>
                      <b>반력 F</b>
                    </td>
                    <td className="cell">
                      <b>{nf(out.reaction.forceN, 0)} N</b>
                    </td>
                    <td className="muted">{out.reaction.equation}</td>
                  </tr>
                </tbody>
              </table>
            </>
          )}

          {(out.warnings.length > 0 ||
            (out.inletCheck?.ok && out.inletCheck.warnings.length > 0) ||
            (out.backpressure?.ok && out.backpressure.warnings.length > 0) ||
            (out.reaction?.ok && out.reaction.warnings.length > 0)) && (
            <>
              <h3 className="sheet__h3">확인 필요 사항</h3>
              <ul className="sheet__warnings">
                {[
                  ...out.warnings,
                  ...(out.inletCheck?.ok ? out.inletCheck.warnings : []),
                  ...(out.backpressure?.ok ? out.backpressure.warnings : []),
                  ...(out.reaction?.ok ? out.reaction.warnings : []),
                ].map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </>
          )}
        </>
      )}

      <footer className="sheet__foot">
        <p>
          근거: API Standard 520 Part II, 6th Edition (2015) · API Standard 520
          Part I, 10th Edition (2020) 식 (1) · KOSHA GUIDE D-63-2018
        </p>
        <p className="muted">
          입구 배관 압력손실은 Darcy–Weisbach 식과 Colebrook–White 마찰계수(KOSHA
          D-63 식 5-10)로 계산했습니다. API 표준은 계산식을 지정하지 않고
          손실에 포함할 항목만 규정합니다 (7.3.7.2). 부속 등가길이(L/D)는
          KOSHA D-63 &lt;표1&gt;, 절대조도는 &lt;표2&gt; 기준이며, 사내
          기준이 있으면 그쪽이 우선합니다. 출구(배출) 배관은 KOSHA D-63
          4.1(1) 에 따라 등온 압축성 식 (5-12)/(5-15) 로 풀며, 마하수는
          5.2.6.2 의 0.8 / 0.6 기준으로 판정합니다. 배관 치수는 KOSHA D-63
          &lt;별표2&gt;(ASME B36.10M/B36.19M) 기준입니다.
        </p>
      </footer>
    </section>
  );
}
